import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { FaSearch, FaDownload, FaTimes } from "react-icons/fa";
import { getApiCall } from "../../service/httpCall";
import Loader from "../../components/partials/components/loader";
import { toast } from "react-toastify";
import { API_DASHBOARD_USER_WISE_CAMPAIGN_SUMMARY_STATS } from "../../service/url";
import { FilterMatchMode } from "primereact/api";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { USER_ROLES } from "../../utilities/constant";

const UserWiseCampaignSummary = () => {
  const today = new Date().toISOString().split("T")[0];

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ startDate: today, endDate: today, userId: "" });
  const [tableFilters, setTableFilters] = useState({
    global:   { value: null, matchMode: FilterMatchMode.CONTAINS },
    userId:   { value: null, matchMode: FilterMatchMode.CONTAINS },
    username: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = await AsyncStorage.getItem("user");
      if (!rawUser) { console.error("User not found in AsyncStorage"); return null; }
      let user;
      try { user = JSON.parse(rawUser); } catch (e) { console.error("Invalid JSON", e); return null; }
      if (user.role) setLoggedInUserRole(user.role);
    }
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole == USER_ROLES.CALL_CENTRE_AGENT) return;
    fetchData();
  }, [loggedInUserRole]);

  const fetchData = () => {
    if (!filters.startDate || !filters.endDate) { toast.error("Start and End date required"); return; }
    setLoading(true);
    let query = `${API_DASHBOARD_USER_WISE_CAMPAIGN_SUMMARY_STATS}?startDate=${filters.startDate}&endDate=${filters.endDate}`;
    if (filters.userId) query += `&userId=${filters.userId}`;
    getApiCall(query)
      .then((resp) => {
        const formatted = (resp.data || resp).map((item) => ({
          ...item,
          userId:   item.userId?.toString(),
          username: item.username?.toString(),
        }));
        setData(formatted);
      })
      .catch((err) => toast.error(err))
      .finally(() => setLoading(false));
  };

  const clearFilters = () => {
    setFilters({ startDate: today, endDate: today, userId: "" });
    setTableFilters({
      global:   { value: null, matchMode: FilterMatchMode.CONTAINS },
      userId:   { value: null, matchMode: FilterMatchMode.CONTAINS },
      username: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setGlobalFilterValue("");
    fetchData();
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setTableFilters((prev) => ({ ...prev, global: { value, matchMode: FilterMatchMode.CONTAINS } }));
  };

  const exportCSV = () => {
    const headers = ["User ID","Username","Total Campaigns","Total Numbers","Calls Dialed","DnD Count","Connected Calls","Pending Calls"];
    const rows = data.map((row) => [row.userId,row.username,row.totalCampaigns,row.totalNumbers,row.callsDialed,row.dndCount,row.connectedCalls,row.pendingCalls]);
    const link = document.createElement("a");
    link.href = encodeURI("data:text/csv;charset=utf-8," + [headers, ...rows].map((e) => e.join(",")).join("\n"));
    link.download = "user-wise-campaign-summary.csv";
    link.click();
  };

  if (loading) return <Loader />;

  return (
    <div className="dash-section-card" style={{ marginBottom: 28 }}>
      <div className="dash-section-header">
        <span>User-Wise Campaign Summary</span>
        {data.length > 0 && (
          <button className="dash-action-btn dash-action-btn--export" onClick={exportCSV} title="Export CSV">
            <FaDownload size={11} /> Export
          </button>
        )}
      </div>

      {/* Filter row */}
      <div className="dash-filter-row">
        <div className="dash-filter-field">
          <label className="dash-filter-label">Start Date</label>
          <input
            type="date"
            className="dash-date-input"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>
        <div className="dash-filter-field">
          <label className="dash-filter-label">End Date</label>
          <input
            type="date"
            className="dash-date-input"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>
        <div className="dash-filter-field">
          <label className="dash-filter-label">User ID</label>
          <input
            type="text"
            className="dash-date-input"
            placeholder="Enter User ID"
            value={filters.userId}
            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
          />
        </div>
        <div className="dash-filter-actions">
          <button className="dash-action-btn dash-action-btn--primary" onClick={fetchData} title="Search">
            <FaSearch size={11} /> Search
          </button>
          <button className="dash-action-btn dash-action-btn--neutral" onClick={clearFilters} title="Clear">
            <FaTimes size={11} /> Clear
          </button>
        </div>
      </div>

      <div className="dash-table-toolbar">
        <div className="dash-search-wrap">
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search by username or user ID…"
            className="dash-search-input"
          />
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <DataTable
          value={data}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          filters={tableFilters}
          onFilter={(e) => setTableFilters(e.filters)}
          globalFilterFields={["userId", "username"]}
          emptyMessage="No data available for the selected date range"
          className="dash-datatable"
        >
          <Column field="userId"          header="User ID"          sortable filter filterPlaceholder="Search ID" />
          <Column field="username"        header="Username"         sortable filter filterPlaceholder="Search Name" />
          <Column field="totalCampaigns"  header="Total Campaigns"  sortable />
          <Column field="totalNumbers"    header="Total Numbers"    sortable />
          <Column field="callsDialed"     header="Calls Dialed"     sortable />
          <Column field="dndCount"        header="DnD Count"        sortable />
          <Column field="connectedCalls"  header="Connected Calls"  sortable />
          <Column field="pendingCalls"    header="Pending Calls"    sortable />
        </DataTable>
      </div>
    </div>
  );
};

export default UserWiseCampaignSummary;
